import { countriesQuery } from "@/api/location";
import { searchSuppliersQuery, useDeleteSupplierMutation } from "@/api/supplier";
import { api } from "@/api/utils";
import { DataTable } from "@/components/data-table/DataTable";
import { DataTableBody } from "@/components/data-table/DataTableBody";
import { DataTableFooter } from "@/components/data-table/DataTableFooter";
import { DataTableHeader } from "@/components/data-table/DataTableHeader";
import { StyledLink } from "@/components/link/StyledLink";
import { Alert } from "@/components/ui/Alert";
import { Button } from "@/components/ui/Button";
import { Dialog, DialogClose } from "@/components/ui/Dialog";
import { DropdownMenu, DropdownMenuItem } from "@/components/ui/DropdownMenu";
import { IconButton } from "@/components/ui/IconButton";
import { PageWrapper } from "@/components/ui/PageWrapper";
import { saveHref } from "@/utils/assume-role";
import { copyToClipboard } from "@/utils/clipboard";
import { getErrorMessage } from "@/utils/error";
import { seo } from "@/utils/seo";
import { Field, Flex, Label, QueryHandler, Select, Text, toast } from "@givenwell/components";
import { Input } from "@/components/ui/Input";
import { CopyIcon, DotsIcon, RecycleBinIcon, UserIcon } from "@givenwell/core-solid-icons";
import { CreateSupplierBody, SupplierSearchResult } from "@givenwell/management-api";
import { useMutation, useQuery } from "@tanstack/react-query";
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { useId, useState } from "react";
import { fallback, number, object, optional, pipe, string, toMinValue } from "valibot";

export const Route = createFileRoute("/admin/suppliers/")({
  component: SuppliersPage,
  validateSearch: object({
    q: fallback(optional(string(), ""), ""),
    country: fallback(optional(string()), undefined),
    page: fallback(pipe(optional(number(), 1), toMinValue(1)), 1),
    "page-size": fallback(pipe(optional(number(), 100), toMinValue(10)), 100),
  }),
  head: () => ({
    meta: seo({ title: "Suppliers" }),
  }),
});

function SuppliersPage() {
  const search = Route.useSearch();
  const { page, "page-size": pageSize, q, country } = search;

  const query = useQuery(searchSuppliersQuery(q, page, pageSize, country));
  const { data: countries } = useQuery(countriesQuery(false));

  const navigate = useNavigate();

  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [targetSupplier, setTargetSupplier] = useState({} as SupplierSearchResult);

  function handleDeleteSupplier(supplier: SupplierSearchResult) {
    // The setTimeout here avoids radix bug with DropDown/Dialog combo leaving the
    // document body with a pointer-events none after closing.
    //
    // See https://github.com/radix-ui/primitives/issues/1241#issuecomment-1815171670
    setTimeout(() => {
      setTargetSupplier(supplier);
      setDeleteModalOpen(true);
    }, 0);
  }

  function handleDeleteDismissed() {
    setTargetSupplier({} as SupplierSearchResult);
    setDeleteModalOpen(false);
  }

  return (
    <PageWrapper title="Suppliers" rightContent={<CreateSupplierModal />}>
      <DeleteSupplierDialog supplier={targetSupplier} isOpen={deleteModalOpen} onDismissed={handleDeleteDismissed} />
      <DataTable>
        <DataTableHeader hideToggle autoFocusSearch>
          <Select
            size="sm"
            css={{ width: 200 }}
            value={country || ""}
            onValueChange={country => {
              navigate({
                to: ".",
                search: {
                  ...search,
                  country: country || undefined,
                },
              });
            }}
          >
            <option value="">Any country</option>
            {countries?.countries
              .filter(country => country.capabilities.suppliers_allowed)
              .map(country => (
                <option key={country.code} value={country.code}>
                  {country.name}
                </option>
              ))}
          </Select>
        </DataTableHeader>
        <DataTableBody
          data={query.data?.items ?? []}
          status={query.status}
          dataKey={"id"}
          columns={[
            {
              header: "Name",
              cell: ({ name, id }) => (
                <StyledLink
                  to="/admin/suppliers/$supplierId"
                  params={{
                    supplierId: id,
                  }}
                >
                  {name}
                </StyledLink>
              ),
            },
            {
              header: "Country",
              cell: ({ country_code }) => <>{countries?.countries.find(c => c.code === country_code)?.name}</>,
            },
            {
              header: "",
              css: {
                textAlign: "right",
              },
              cell: supplier => (
                <>
                  <DropdownMenu
                    align="end"
                    content={
                      <>
                        <DropdownMenuItem
                          onSelect={() => {
                            saveHref();
                            navigate({
                              to: "/supplier/$supplierId",
                              params: {
                                supplierId: supplier.id,
                              },
                            });
                          }}
                          icon={<UserIcon />}
                        >
                          Assume role
                        </DropdownMenuItem>
                        <DropdownMenuItem onSelect={() => copyToClipboard(supplier.id)} icon={<CopyIcon />}>
                          Copy supplier ID
                        </DropdownMenuItem>
                        <DropdownMenuItem
                          color="red"
                          icon={<RecycleBinIcon />}
                          onSelect={() => handleDeleteSupplier(supplier)}
                        >
                          Delete supplier
                        </DropdownMenuItem>
                      </>
                    }
                  >
                    <IconButton
                      aria-label="Actions"
                      css={{
                        opacity: 0,
                        transition: "opacity 0s",
                        "&:hover, td:hover>&, tr:hover > td > &": {
                          opacity: 1,
                        },
                        "&[data-active], &[data-state=open]": {
                          opacity: 1,
                        },
                      }}
                      variant="plain"
                      color="neutral"
                      size="sm"
                    >
                      <DotsIcon />
                    </IconButton>
                  </DropdownMenu>
                </>
              ),
            },
          ]}
        />
        <DataTableFooter itemCount={query.data?.total_items} />
      </DataTable>
    </PageWrapper>
  );
}

function CreateSupplierModal() {
  const query = useQuery(countriesQuery(false));
  const id = useId();
  const navigate = useNavigate();

  const [name, setName] = useState("");
  const [country, setCountry] = useState("");
  const [primaryIntent, setPrimaryIntent] = useState<NonNullable<CreateSupplierBody["primary_intent"]>>("marketplace");

  const createMutation = useMutation({
    mutationFn: async () => {
      const supplier = await api.supplier.createSupplier({
        requestBody: {
          country,
          name,
          primary_intent: primaryIntent,
        },
      });
      return supplier;
    },
    onSuccess(data) {
      toast.success("Supplier created!");
      navigate({
        to: "/admin/suppliers/$supplierId",
        params: {
          supplierId: data.id,
        },
      });
    },
    onError(error) {
      toast.error("Error creating supplier", { description: getErrorMessage(error) });
    },
  });

  const handleCreate = () => {
    createMutation.mutate(undefined);
  };

  function isValid(): boolean {
    return !createMutation.isSuccess && !!name.trim() && !!country;
  }

  return (
    <Dialog
      title="Create a supplier"
      content={
        <QueryHandler
          query={query}
          success={({ data }) => {
            return (
              <form
                id={id}
                onSubmit={e => {
                  e.preventDefault();
                  handleCreate();
                }}
              >
                <Flex css={{ flexDir: "column", gap: 24, items: "center" }}>
                  <Field>
                    <Label>Name</Label>
                    <Input autoFocus required value={name} onValueChange={setName} className="ph-clear" />
                  </Field>
                  <Field>
                    <Label>Country</Label>
                    <Select value={country} onValueChange={setCountry}>
                      <option key="" value="" disabled={true}>
                        Select a country
                      </option>
                      {data.countries
                        .filter(c => c.capabilities.suppliers_allowed)
                        .map(country => (
                          <option key={country.code} value={country.code}>
                            {country.name}
                          </option>
                        ))}
                    </Select>
                  </Field>
                  <Field>
                    <Label>Primary intent</Label>
                    <Select
                      value={primaryIntent}
                      onValueChange={value => setPrimaryIntent(value as "marketplace" | "eap")}
                    >
                      <option value="marketplace">Marketplace</option>
                      <option value="eap">EAP</option>
                    </Select>
                  </Field>
                </Flex>
              </form>
            );
          }}
        />
      }
      footer={
        <Button
          type="submit"
          variant="tonal"
          color="primary"
          form={id}
          loading={createMutation.isPending}
          disabled={!isValid()}
          loadingText="Creating..."
        >
          Create
        </Button>
      }
    >
      <Button variant="tonal" color="primary">
        Create a supplier
      </Button>
    </Dialog>
  );
}

type DeleteSupplierDialogProps = {
  supplier: SupplierSearchResult;
  isOpen: boolean;
  onDismissed: () => void;
};
function DeleteSupplierDialog(props: DeleteSupplierDialogProps) {
  const deleteSupplierMut = useDeleteSupplierMutation();

  function handleOnOpenChange(open: boolean) {
    if (!open) {
      props.onDismissed();
    }
  }

  async function handleDeleteClick() {
    try {
      await deleteSupplierMut.mutateAsync({ supplierId: props.supplier.id });
      toast.success("Supplier deleted");
      props.onDismissed();
      setConfirmSupplierName("");
    } catch (error) {
      toast.error("Error deleting supplier", { description: getErrorMessage(error) });
    }
  }

  const [confirmSupplierName, setConfirmSupplierName] = useState("");

  return (
    <Dialog
      open={props.isOpen}
      onOpenChange={handleOnOpenChange}
      title="Delete Supplier"
      content={
        <Flex css={{ gap: 20, flexDir: "column" }}>
          <Text css={{ textAlign: "center" }}>
            Are you <em>absolutely</em> sure you want to <em>permanently</em> delete{" "}
            <strong>{props.supplier.name}</strong>?
          </Text>
          <Alert status="warning">Warning – this action is permanent and cannot be reversed.</Alert>
          <Flex css={{ flexDir: "column", gap: 8 }}>
            <Text>To confirm, type the name of the supplier below:</Text>
            <Input
              placeholder="Type the supplier name"
              value={confirmSupplierName}
              onValueChange={setConfirmSupplierName}
            />
          </Flex>
        </Flex>
      }
      footer={
        <>
          <DialogClose>
            <Button variant="plain" color="neutral" css={{ flex: 1 }}>
              Cancel
            </Button>
          </DialogClose>
          <Button
            variant="filled"
            color="red"
            disabled={confirmSupplierName !== props.supplier.name}
            css={{ flex: 1 }}
            loading={deleteSupplierMut.isPending}
            onClick={handleDeleteClick}
          >
            Delete
          </Button>
        </>
      }
    />
  );
}
